import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Modal from '../ui/Modal';
import LoadingSpinner from '../ui/Spinner';
import { getAccessToken } from './adminUtils';
import PayoutModal from './PayoutModal';
import './AdminTableView.css';

/**
 * Admin Table View - Comprehensive table management interface
 */
const AdminTableView = ({ tableName, tableConfig, onBack }) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters and sorting
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Edit modal
  const [editModal, setEditModal] = useState({ open: false, record: null });
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Payout modal
  const [payoutModal, setPayoutModal] = useState({ open: false, order: null });

  useEffect(() => {
    fetchData();
  }, [tableName, currentPage, pageSize, search, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAccessToken();

      // Special handling for orders - fetch from external API
      if (tableName === 'received_orders') {
        const response = await fetch('/api/admin/checkout-orders/', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch orders (${response.status})`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Failed to fetch orders');
        }

        // Define columns for orders view
        const orderColumns = [
          { key: 'id', label: 'Order ID', type: 'number' },
          { key: 'order_id', label: 'Order UUID', type: 'text' },
          { key: 'customer_id', label: 'Customer ID', type: 'number' },
          { key: 'designer_id', label: 'Designer ID', type: 'number' },
          { key: 'productname', label: 'Product Name', type: 'text' },
          { key: 'price', label: 'Price (₹)', type: 'text' },
          { key: 'final_price', label: 'Final Price (₹)', type: 'text' },
          { key: 'name', label: 'Customer Name', type: 'text' },
          { key: 'phonenumber', label: 'Phone', type: 'text' },
          { key: 'city', label: 'City', type: 'text' },
          { key: 'payout_created', label: 'Payout Status', type: 'boolean' }
        ];

        setData(result.orders || []);
        setColumns(orderColumns);
        setPagination({
          total_items: result.total || 0,
          total_pages: 1,
          current_page: 1,
          has_previous: false,
          has_next: false
        });

        console.log(`Successfully loaded ${result.orders?.length || 0} orders from external API`);
        return;
      }

      // Default handling for other tables
      const params = new URLSearchParams({
        table: tableName,
        page: currentPage,
        page_size: pageSize,
        search,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      const response = await fetch(`/api/admin/table-data/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch ${tableName} data (${response.status})`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      setData(result.data || []);
      setColumns(result.columns || []);
      setPagination(result.pagination || {});

      console.log(`Successfully loaded ${result.data?.length || 0} records for ${tableName}`);
    } catch (err) {
      console.error('Error fetching table data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditData({ ...record });
    setEditModal({ open: true, record });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = getAccessToken();

      // Clean up the edit data - only send changed fields
      const cleanedUpdates = {};
      Object.keys(editData).forEach(key => {
        const newValue = editData[key];
        const originalValue = editModal.record[key];

        // Only include if value has changed
        if (newValue !== originalValue) {
          cleanedUpdates[key] = newValue;
        }
      });

      console.log('Sending updates:', cleanedUpdates);

      const response = await fetch('/api/admin/update-record/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          table: tableName,
          id: editModal.record.id,
          updates: cleanedUpdates,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update record (${response.status})`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      // Update local data
      setData(prev => prev.map(item =>
        item.id === editModal.record.id ? result.data : item
      ));

      setEditModal({ open: false, record: null });
      setEditData({});

      // Show success message
      alert(`Record updated successfully! Updated fields: ${result.updated_fields?.join(', ') || 'none'}`);
    } catch (err) {
      console.error('Error updating record:', err);
      alert('Failed to update record: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Are you sure you want to delete this ${tableName.slice(0, -1)}?`)) {
      return;
    }

    try {
      const token = getAccessToken();

      const response = await fetch(`/api/admin/delete-record/${tableName}/${record.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete record');
      }

      // Remove from local data
      setData(prev => prev.filter(item => item.id !== record.id));
    } catch (err) {
      console.error('Error deleting record:', err);
      alert('Failed to delete record: ' + err.message);
    }
  };

  const handleCreatePayout = (order) => {
    // Check if designer_id exists
    if (!order.designer_id) {
      alert('⚠️ Cannot create payout: Order has no designer assigned');
      return;
    }

    // Open payout modal
    setPayoutModal({ open: true, order });
  };

  const handleConfirmPayout = async (orderId) => {
    const token = getAccessToken();

    const response = await fetch('/api/admin/payouts/create/', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ order_id: orderId }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.message || 'Failed to create payout');
    }

    // Refresh data to update payout_created status
    fetchData();

    return result.data;
  };

  const handleClosePayoutModal = () => {
    setPayoutModal({ open: false, order: null });
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const formatCellValue = (value, type) => {
    if (value === null || value === undefined || value === '') {
      return <span className="admin-table__empty">—</span>;
    }

    switch (type) {
      case 'boolean':
        return (
          <span className={`admin-table__boolean ${value ? 'true' : 'false'}`}>
            {value ? '✓' : '✗'}
          </span>
        );
      case 'datetime':
        return new Date(value).toLocaleString();
      case 'email':
        return <a href={`mailto:${value}`} className="admin-table__email">{value}</a>;
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      default:
        return value;
    }
  };

  if (loading) {
    return (
      <div className="admin-table-view admin-table-view--loading">
        <LoadingSpinner size="large" />
        <p>Loading {tableConfig?.label} data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-table-view admin-table-view--error">
        <Card className="admin-error-card">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <Button onClick={fetchData}>Retry</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-table-view">
      {/* Header */}
      <div className="admin-table-view__header">
        <div className="admin-table-view__title-section">
          <h1 className="admin-table-view__title">
            <span className="admin-table-view__icon">{tableConfig?.icon}</span>
            {tableConfig?.label}
          </h1>
          <p className="admin-table-view__description">{tableConfig?.description}</p>
        </div>

        <div className="admin-table-view__actions">
          <Button variant="secondary" onClick={onBack}>
            ← Back to Overview
          </Button>
          <Button onClick={fetchData}>
            🔄 Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="admin-table-view__filters">
        <div className="admin-filters">
          <div className="admin-filters__search">
            <Input
              placeholder={`Search ${tableConfig?.label}...`}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              icon="🔍"
            />
          </div>

          <div className="admin-filters__controls">
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              options={[
                { value: 10, label: '10 per page' },
                { value: 20, label: '20 per page' },
                { value: 50, label: '50 per page' },
                { value: 100, label: '100 per page' },
              ]}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="admin-table-view__table-card">
        {data.length === 0 ? (
          <div className="admin-table-view__empty">
            <div className="admin-table-view__empty-icon">📭</div>
            <h3>No {tableConfig?.label} Found</h3>
            <p>No data matches your current filters.</p>
          </div>
        ) : (
          <>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead className="admin-table__head">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={`admin-table__header ${
                          sortBy === column.key ? 'sorted' : ''
                        }`}
                        onClick={() => handleSort(column.key)}
                      >
                        <div className="admin-table__header-content">
                          <span>{column.label}</span>
                          {sortBy === column.key && (
                            <span className="admin-table__sort-indicator">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="admin-table__header admin-table__header--actions">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="admin-table__body">
                  <AnimatePresence>
                    {data.map((row, index) => (
                      <motion.tr
                        key={row.id}
                        className="admin-table__row"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                      >
                        {columns.map((column) => (
                          <td key={column.key} className="admin-table__cell">
                            {formatCellValue(row[column.key], column.type)}
                          </td>
                        ))}
                        <td className="admin-table__cell admin-table__cell--actions">
                          <div className="admin-table__actions">
                            {tableName === 'received_orders' ? (
                              // Special actions for orders table
                              <>
                                {row.payout_created ? (
                                  <span className="admin-table__payout-badge" title="Payout already created">
                                    ✅ Paid
                                  </span>
                                ) : (
                                  <button
                                    className="admin-table__action admin-table__action--payout"
                                    onClick={() => handleCreatePayout(row)}
                                    title="Create payout for designer"
                                  >
                                    💰 Payout
                                  </button>
                                )}
                              </>
                            ) : (
                              // Default actions for other tables
                              <>
                                <button
                                  className="admin-table__action admin-table__action--edit"
                                  onClick={() => handleEdit(row)}
                                  title="Edit"
                                >
                                  ✏️
                                </button>
                                <button
                                  className="admin-table__action admin-table__action--delete"
                                  onClick={() => handleDelete(row)}
                                  title="Delete"
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="admin-table-view__pagination">
                <div className="admin-pagination">
                  <div className="admin-pagination__info">
                    Showing {((currentPage - 1) * pageSize) + 1} to{' '}
                    {Math.min(currentPage * pageSize, pagination.total_items)} of{' '}
                    {pagination.total_items} entries
                  </div>

                  <div className="admin-pagination__controls">
                    <Button
                      variant="outline"
                      disabled={!pagination.has_previous}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      Previous
                    </Button>

                    {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                      const page = Math.max(1, currentPage - 2) + i;
                      if (page > pagination.total_pages) return null;

                      return (
                        <Button
                          key={page}
                          variant={page === currentPage ? 'primary' : 'outline'}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      );
                    })}

                    <Button
                      variant="outline"
                      disabled={!pagination.has_next}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Edit Modal */}
      <Modal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, record: null })}
        title={`Edit ${tableConfig?.label.slice(0, -1)}`}
        size="large"
      >
        <div className="admin-edit-modal">
          <div className="admin-edit-form">
            {columns.map((column) => {
              if (column.key === 'id') return null;

              // Skip read-only fields
              const readOnlyFields = ['user_username', 'user_email', 'product_name', 'designer_name', 'order_name'];
              if (readOnlyFields.includes(column.key)) return null;

              const renderField = () => {
                switch (column.type) {
                  case 'boolean':
                    return (
                      <Select
                        value={editData[column.key] ? 'true' : 'false'}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          [column.key]: e.target.value === 'true'
                        }))}
                        options={[
                          { value: 'true', label: 'Yes (True)' },
                          { value: 'false', label: 'No (False)' },
                        ]}
                      />
                    );

                  case 'datetime':
                    return (
                      <Input
                        type="datetime-local"
                        value={editData[column.key] ? new Date(editData[column.key]).toISOString().slice(0, 16) : ''}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          [column.key]: e.target.value
                        }))}
                        placeholder="Select date and time"
                      />
                    );

                  case 'email':
                    return (
                      <Input
                        type="email"
                        value={editData[column.key] || ''}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          [column.key]: e.target.value
                        }))}
                        placeholder="Enter email address"
                      />
                    );

                  case 'number':
                    return (
                      <Input
                        type="number"
                        value={editData[column.key] || ''}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          [column.key]: e.target.value
                        }))}
                        placeholder="Enter number"
                      />
                    );

                  default:
                    // Check if it's a large text field
                    if (column.key.includes('description') || column.key.includes('address') || column.key.includes('comments')) {
                      return (
                        <textarea
                          className="admin-edit-textarea"
                          value={editData[column.key] || ''}
                          onChange={(e) => setEditData(prev => ({
                            ...prev,
                            [column.key]: e.target.value
                          }))}
                          placeholder={`Enter ${column.label.toLowerCase()}`}
                          rows={3}
                        />
                      );
                    }

                    return (
                      <Input
                        type="text"
                        value={editData[column.key] || ''}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          [column.key]: e.target.value
                        }))}
                        placeholder={`Enter ${column.label.toLowerCase()}`}
                      />
                    );
                }
              };

              return (
                <div key={column.key} className="admin-edit-field">
                  <label className="admin-edit-label">
                    {column.label}
                    <span className="admin-edit-field-type">({column.type})</span>
                  </label>
                  {renderField()}
                  {column.key === 'email' && (
                    <small className="admin-edit-hint">Enter a valid email address</small>
                  )}
                  {column.type === 'boolean' && (
                    <small className="admin-edit-hint">Choose Yes (true) or No (false)</small>
                  )}
                  {column.type === 'datetime' && (
                    <small className="admin-edit-hint">Select date and time</small>
                  )}
                </div>
              );
            })}
          </div>

          <div className="admin-edit-actions">
            <Button
              variant="secondary"
              onClick={() => setEditModal({ open: false, record: null })}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payout Modal */}
      {payoutModal.open && (
        <PayoutModal
          order={payoutModal.order}
          onClose={handleClosePayoutModal}
          onConfirm={handleConfirmPayout}
        />
      )}
    </div>
  );
};

export default AdminTableView;